/**
 * WalkSegmentOverlay — renders inter-station walking-leg pictograms over
 * the WebGL map. 1:1 port of iOS
 * `metro-ios/MetroModules/Sources/MetroMap/Views/WalkSegmentOverlay.swift`.
 *
 * Each segment is two endpoint pictograms ("sandwich" of pad ø 31 +
 * grey-stroked ring ø 27 with stroke 3) plus N small dots (ø 6, fixed
 * #D9D9D9) distributed evenly along the straight line between them. An
 * end that lands on a transfer pill the route highlight draws keeps no
 * sandwich — the pill already marks that stop (owner 2026-10-20). All
 * sizes are canvas-space at zoom 1.0; they scale linearly with the
 * current map zoom so the pictogram density stays constant relative to
 * the surrounding map glyphs regardless of pinch level.
 *
 * Companion to RouteBubbleOverlay: bubbles mark the user-selected
 * A / B endpoints, this overlay marks walking segments INSIDE the
 * selected route. Same imperative `apply(zoom, panX, panY, dpr)` rAF
 * contract as the bubble overlay so the host store keeps a single
 * batched-update path.
 */
import { forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react'
import type { Route } from '@metro/routing'
import type { SchemaData } from '../schema/types'
import { canvasToScreen } from '../interaction/canvasToScreen'
import {
  buildDrawnTransferStopIds,
  buildStationCoordIndex,
  buildTransitionIdSet,
  extractWalkSegmentsWithIndex,
  type WalkSegment,
} from '../state/extractWalkSegments'
import styles from './WalkSegmentOverlay.module.css'

// Canvas-space pictogram constants. Match the iOS struct fields exactly
// (`WalkSegmentOverlay.swift:38–62`).
const ENDPOINT_PAD_DIAMETER = 31
const ENDPOINT_RING_DIAMETER = 27
const ENDPOINT_RING_STROKE_WIDTH = 3
const INTERMEDIATE_DOT_DIAMETER = 6
const CANVAS_DOT_SPACING = 18
const MAX_INTERMEDIATE_DOTS = 24
const NO_IDS: ReadonlySet<string> = new Set()

export interface WalkSegmentOverlayHandle {
  /** Called each rAF tick with the current transform; updates SVG
   *  geometry imperatively. */
  apply(zoom: number, panX: number, panY: number, dpr: number): void
}

interface Props {
  /** Currently-selected route. Pass `null` to hide. */
  route: Route | null
  /** Schema snapshot for station coord lookup. */
  schema: SchemaData
  /**
   * Element ids the route highlight draws. A walk end on a stop that a
   * highlighted, drawn transfer covers loses its endpoint sandwich.
   */
  highlightedIds?: ReadonlySet<string>
  /**
   * Getter for the current map transform. Called once on mount AFTER
   * the SVG nodes exist so the overlay is positioned correctly BEFORE
   * first paint — otherwise the circles flash at (0,0) for one frame
   * until the host's next rAF tick fires `apply()`.
   */
  getTransform: () => { zoom: number; panX: number; panY: number; dpr: number }
}

interface ResolvedSegment {
  readonly fromX: number
  readonly fromY: number
  readonly toX: number
  readonly toY: number
  readonly fromRing: boolean
  readonly toRing: boolean
  readonly nDots: number
}

interface CircleSpec {
  readonly cx: number
  readonly cy: number
  readonly canvasRadius: number
  readonly className: string
  readonly strokeCanvasWidth?: number
}

/**
 * Pre-compute the per-segment dot count + position formula. Mirrors iOS
 * `_segment` (`WalkSegmentOverlay.swift:75–127`):
 *   - `nDots = max(1, min(floor(distance / spacing), 24))` so a very short
 *     walk still draws one intermediate (the pictogram doesn't collapse
 *     to two touching pads) and a very long walk caps at 24 dots.
 *   - midpoint parameter `t = i / (nDots + 1)` — indexing 1..nDots keeps
 *     the dot cadence symmetric about the segment midpoint.
 */
function resolveSegment(seg: WalkSegment): ResolvedSegment {
  const dx = seg.toX - seg.fromX
  const dy = seg.toY - seg.fromY
  const dist = Math.sqrt(dx * dx + dy * dy)
  const raw = Math.floor(dist / CANVAS_DOT_SPACING)
  const nDots = Math.max(1, Math.min(raw, MAX_INTERMEDIATE_DOTS))
  return { ...seg, nDots }
}

/** Build the flat list of circle specs for one segment. Endpoints come
 *  AFTER intermediates so they render on top (z-order via DOM order). */
function buildCircles(seg: ResolvedSegment): CircleSpec[] {
  const circles: CircleSpec[] = []
  const dx = seg.toX - seg.fromX
  const dy = seg.toY - seg.fromY
  for (let i = 1; i <= seg.nDots; i++) {
    const t = i / (seg.nDots + 1)
    circles.push({
      cx: seg.fromX + dx * t,
      cy: seg.fromY + dy * t,
      canvasRadius: INTERMEDIATE_DOT_DIAMETER / 2,
      className: styles.dot,
    })
  }
  // Endpoint sandwich — pad (Ø31, theme backdrop) then ring (Ø27, stroked
  // grey, same backdrop fill so the centre reads hollow on the dimmed map).
  for (const end of [
    { cx: seg.fromX, cy: seg.fromY, ring: seg.fromRing },
    { cx: seg.toX, cy: seg.toY, ring: seg.toRing },
  ]) {
    if (!end.ring) continue
    circles.push({
      cx: end.cx,
      cy: end.cy,
      canvasRadius: ENDPOINT_PAD_DIAMETER / 2,
      className: styles.pad,
    })
    circles.push({
      cx: end.cx,
      cy: end.cy,
      canvasRadius: ENDPOINT_RING_DIAMETER / 2,
      className: styles.ring,
      strokeCanvasWidth: ENDPOINT_RING_STROKE_WIDTH,
    })
  }
  return circles
}

export const WalkSegmentOverlay = forwardRef<WalkSegmentOverlayHandle, Props>(
  function WalkSegmentOverlay({ route, schema, highlightedIds = NO_IDS, getTransform }, ref) {
    const hostRef = useRef<HTMLDivElement | null>(null)
    const circleRefs = useRef<(SVGCircleElement | null)[]>([])
    const specsRef = useRef<CircleSpec[]>([])

    // Schema → station coord index is stable per schema load. Build it
    // once and reuse across rapid route-variant switches; the
    // per-route hot path then just does O(walkLegs) lookups instead of
    // rebuilding a 600-entry Map every render.
    const stationCoordIndex = useMemo(() => buildStationCoordIndex(schema), [schema])
    // Transition ids the schema can render as a pill — a walk leg whose
    // transition_id is NOT in this set gets the pictogram drawn (street
    // walk between cluster siblings), instead of being skipped.
    const transitionIdSet = useMemo(() => buildTransitionIdSet(schema), [schema])
    const drawnTransferStopIds = useMemo(
      () => buildDrawnTransferStopIds(highlightedIds, schema),
      [highlightedIds, schema],
    )

    const specs = useMemo<CircleSpec[]>(() => {
      if (route === null) return []
      const segments = extractWalkSegmentsWithIndex(
        route,
        stationCoordIndex,
        transitionIdSet,
        drawnTransferStopIds,
      ).map(resolveSegment)
      const out: CircleSpec[] = []
      for (const seg of segments) out.push(...buildCircles(seg))
      return out
    }, [route, stationCoordIndex, transitionIdSet, drawnTransferStopIds])

    // Keep the imperative `apply()` aware of the current spec list — when
    // route changes the slot array also changes so refs need to align.
    // useLayoutEffect (not direct assignment) keeps React happy about
    // ref mutation timing: write happens AFTER commit, BEFORE the next
    // rAF tick, so `apply()` always sees the post-render spec list.
    useLayoutEffect(() => {
      specsRef.current = specs
    }, [specs])

    useImperativeHandle(
      ref,
      () => ({
        apply: (zoom, panX, panY, dpr) => {
          const refs = circleRefs.current
          const arr = specsRef.current
          for (let i = 0; i < arr.length; i++) {
            const spec = arr[i]
            const node = refs[i]
            if (node === null || node === undefined) continue
            const screen = canvasToScreen(spec.cx, spec.cy, panX, panY, zoom)
            // canvasToScreen returns physical pixels; the SVG renders in
            // CSS pixels, so divide by dpr to land at the right CSS
            // coordinate. Sizes scale by zoom too (canvas-space radius
            // → physical-pixel radius → CSS-pixel radius).
            const cxCss = screen.x / dpr
            const cyCss = screen.y / dpr
            const rPx = spec.canvasRadius * zoom
            const rCss = rPx / dpr
            node.setAttribute('cx', String(cxCss))
            node.setAttribute('cy', String(cyCss))
            node.setAttribute('r', String(rCss))
            if (spec.strokeCanvasWidth !== undefined) {
              const swPx = spec.strokeCanvasWidth * zoom
              node.setAttribute('stroke-width', String(swPx / dpr))
            }
          }
        },
      }),
      [],
    )

    // Pre-position circles BEFORE first paint so they don't flash at (0,0)
    // for one frame while the host catches up. Mirrors the
    // `useLayoutEffect` pattern in RouteBubbleOverlay.
    const tf = getTransform()

    return (
      <div ref={hostRef} className={styles.host} aria-hidden>
        <svg className={styles.svg}>
          {specs.map((spec, i) => {
            const screen = canvasToScreen(spec.cx, spec.cy, tf.panX, tf.panY, tf.zoom)
            const cxCss = screen.x / tf.dpr
            const cyCss = screen.y / tf.dpr
            const rPx = spec.canvasRadius * tf.zoom
            const rCss = rPx / tf.dpr
            const swCss =
              spec.strokeCanvasWidth !== undefined
                ? (spec.strokeCanvasWidth * tf.zoom) / tf.dpr
                : undefined
            return (
              <circle
                key={`walk-${i}`}
                ref={(el) => {
                  circleRefs.current[i] = el
                }}
                className={spec.className}
                cx={cxCss}
                cy={cyCss}
                r={rCss}
                {...(swCss !== undefined ? { strokeWidth: swCss } : {})}
              />
            )
          })}
        </svg>
      </div>
    )
  },
)
